import React, { useState } from 'react'
import '../styles/AdminLogout.css'
import '../styles/SharedLayout.css'
import { Layout, Button, Space, Typography, Card} from 'antd'
import { LogoutOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { clearUserSession } from '../utilities/commons'
import AdminSidebar from '../components/AdminSidebar'
import { postRequest } from '../utilities/apicalls'
import ToastMessage from '../components/ToastMessage'

const { Content } = Layout
const { Title, Text } = Typography

/**
 * AdminLogout Component
 * 
 * Displays the logout confirmation page with navigation sidebar for admin users
 * @returns {JSX.Element} The AdminLogout component
 */
const AdminLogout = () => {
	const history = useHistory();
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

	// Event handlers
	const handleCancel = () => {
		history.push('/admin-acknowledgment-receipt');
	};

	// Handle sidebar collapse
	const handleSidebarCollapse = (collapsed) => {
		setSidebarCollapsed(collapsed);
	};

	// Logout handler with API connection
	const handleLogout = async () => {
		try {
			// Get the required values from localStorage
			const id = localStorage.getItem('id');
			const token = localStorage.getItem('token');

			if (!id) {
				// console.error('Admin ID is missing');
				ToastMessage.error('Unable to logout: Missing admin ID', 5);
				return;
			}

			if (!token) {
				// console.error('Auth token is missing');
				ToastMessage.error('Unable to logout: Missing authentication token', 5);
				return;
			}

			// Create a FormData object for the API request
			const formData = new FormData();
			formData.append('token', token);
			formData.append('requester', id);
			
			// API call to logout the user using FormData
			const response = await postRequest('logout', formData, true, true); // true, true for FormData
			
			// console.log('API Response:', response);

			if (response.status === 200) {
				// Always clear the session - even if the API call fails
				clearUserSession();
				
				// Report success to the user
				ToastMessage.success('Logged out successfully', 3);
				
				// Always redirect to login page
				setTimeout(() => {
					history.push('/admin-login');
				}, 1500);
			} else {
				// Handle non-200 response
				const errorMessage = response?.messages?.error_messages?.[0] || 'Failed to logout. Please try again.';
				ToastMessage.error(errorMessage);
			}
		} catch (error) {
			// console.error('Logout error:', error);
			
			// Handle API errors using our consistent pattern
			const errorMessage = error?.messages?.error_messages?.[0] || 'Failed to connect to server. Logging out locally.';
			
			// Force logout on client side even if API fails
			clearUserSession();
			ToastMessage.warning(errorMessage, 3);
			setTimeout(() => {
				history.push('/admin-login');
			}, 1500);
		}
	};

	return (
		<div className="page-wrapper">
			<Layout className='page-body'>
				<AdminSidebar activePage="logout" onCollapse={handleSidebarCollapse} />
				<Content className={`alo-main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
					<div className="alo-content-wrapper">
						{/* Logout Confirmation */}
						<div className='alo-logout-container'>
							<Card className='alo-logout-box'>
								<LogoutOutlined className='alo-logout-icon' />
								<Title level={4} className='alo-logout-title'>Confirm Logout</Title>
								<Text className='alo-logout-message'>
									Are you sure you want to log out from your admin account? 
								</Text>
								<Space className='alo-logout-actions'>
									<Button
										size="large"
										onClick={handleCancel}
										className='alo-cancel-button'
									>
										Cancel
									</Button>
									<Button
										type="primary"
										size="large"
										onClick={handleLogout}
										className='alo-confirm-button'
									>
										Logout
									</Button>
								</Space>
							</Card>
						</div>

						{/* Company Branding */}
						<div className="copyright-footer">
							©2025 MYT Softdev Solutions Inc.
						</div>
					</div>
				</Content>
			</Layout>
		</div>
	);
};

export default AdminLogout; 